import { useMemo, useState } from 'react';
import useWorldStore from '../store/worldStore';
import {
  areEquivalentAgentIds,
  findWorldAgentById,
  resolveWorldAgentId,
} from '../lib/agentIdentity.mjs';
import { getTaskIdentity } from '../lib/taskIdentity';
import TaskComposerCard from './TaskComposerCard';
import { getTaskComposerPlaceholder } from './taskComposerConfig';
import { queueTaskAndKickoff, runAgentQueue } from '../lib/taskQueueActions';

const mono = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };
const COMPLETED_TASK_PAGE_SIZE = 25;
const COMPLETED_TASK_RESULT_PREVIEW_LIMIT = 1200;

function timeAgo(ts) {
  if (!ts) return '';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function findAgent(agents, agentId) {
  return findWorldAgentById(agentId, agents);
}

function ActiveTaskCard({ task, agent }) {
  const color = agent?.color || '#888';
  const lastStep = task.steps?.[task.steps.length - 1];

  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      borderLeft: `3px solid ${color}`,
      borderRadius: '0 6px 6px 0',
      padding: '8px 10px', marginBottom: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
        <span style={{ ...mono, fontSize: 10, color: 'white', fontWeight: 500 }}>{agent?.name}</span>
        <span style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.25)' }}>{agent?.role}</span>
      </div>
      <div style={{ ...mono, fontSize: 11, color: 'white', fontWeight: 500, marginBottom: 4 }}>
        {task.label}
      </div>
      {lastStep && (
        <div style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.35)' }}>
          Step {task.steps.length} · {lastStep.toolEmoji || '⚡'} {lastStep.toolLabel || lastStep.tool}
        </div>
      )}
      {/* Indeterminate progress bar */}
      <div style={{ height: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 1, marginTop: 6, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: '30%', background: color,
          animation: 'progressSlide 1.5s ease-in-out infinite',
        }} />
      </div>
      <div style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.2)', marginTop: 4 }}>
        Started {timeAgo(task.startedAt)}
      </div>

      <style>{`
        @keyframes progressSlide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(430%); }
        }
      `}</style>
    </div>
  );
}

function OptimisticToolUseCard({ toolUse, agent }) {
  const color = agent?.color || '#888';
  const label = toolUse?.label || toolUse?.instruction || 'Working in OpenClaw';

  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      borderLeft: `3px solid ${color}`,
      borderRadius: '0 6px 6px 0',
      padding: '8px 10px', marginBottom: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
        <span style={{ ...mono, fontSize: 10, color: 'white', fontWeight: 500 }}>{agent?.name}</span>
        <span style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.25)' }}>Agent</span>
      </div>
      <div style={{ ...mono, fontSize: 11, color: 'white', fontWeight: 500, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.35)' }}>
        {toolUse?.toolEmoji || '⚡'} {toolUse?.toolLabel || 'OpenClaw'}
      </div>
      <div style={{ height: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 1, marginTop: 6, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: '30%', background: color,
          animation: 'progressSlide 1.5s ease-in-out infinite',
        }} />
      </div>
      <div style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.2)', marginTop: 4 }}>
        Starting OpenClaw...
      </div>
    </div>
  );
}

function QueuedTaskCard({ task, agent, onRunNow, onEdit, onDelete }) {
  const color = agent?.color || '#888';
  const [editing, setEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(task.label);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.015)',
      borderLeft: `3px solid ${color}44`,
      borderRadius: '0 6px 6px 0',
      padding: '8px 10px', marginBottom: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, opacity: 0.6 }} />
        <span style={{ ...mono, fontSize: 9, color }}>{agent?.name}</span>
      </div>
      {editing ? (
        <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { onEdit(task.id, task.instruction, editLabel); setEditing(false); }
            if (e.key === 'Escape') setEditing(false);
          }}
          autoFocus
          style={{
            width: '100%', ...mono, fontSize: 10, color: 'white',
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 4, padding: '2px 6px', outline: 'none', marginBottom: 3,
          }}
        />
      ) : (
        <div style={{ ...mono, fontSize: 10, color: 'rgba(255,255,255,0.7)', marginBottom: 3 }}>
          {task.label}
        </div>
      )}
      <div style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.2)', marginBottom: 4 }}>
        Waiting · added {timeAgo(task.createdAt)}
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <button onClick={() => onRunNow(task)} style={actionBtn(color)}>▶ Run Now</button>
        <button onClick={() => setEditing(true)} style={actionBtn('rgba(255,255,255,0.4)')}>✎ Edit</button>
        <button onClick={() => onDelete(task.id)} style={actionBtn('#ef4444')}>✕</button>
      </div>
    </div>
  );
}

function CompletedTaskCard({ task, agent, onView }) {
  const color = agent?.color || '#888';
  const [expanded, setExpanded] = useState(false);
  const isComplete = task.status === 'complete';
  const isCancelled = task.status === 'cancelled';
  const summary = typeof task.summary === 'string' ? task.summary.trim() : '';
  const statusColor = isComplete ? '#4ade80' : isCancelled ? 'rgba(255,255,255,0.45)' : '#ef4444';
  const borderColor = isComplete ? '#4ade8044' : isCancelled ? 'rgba(255,255,255,0.12)' : '#ef444444';
  const statusIcon = isComplete ? '✓' : isCancelled ? '•' : '✗';

  return (
    <div style={{
      padding: '5px 10px', marginBottom: 3,
      borderLeft: `2px solid ${borderColor}`,
      borderRadius: '0 4px 4px 0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: color, opacity: 0.5 }} />
        <span style={{ ...mono, fontSize: 9, color: 'rgba(255,255,255,0.5)', flex: 1 }}>
          {agent?.name} · {task.label}
        </span>
        <button onClick={(e) => { e.stopPropagation(); onView?.(); }} style={{
          background: 'none', border: '1px solid rgba(255,255,255,0.1)',
          ...mono, fontSize: 7, color: 'rgba(255,255,255,0.35)',
          borderRadius: 4, padding: '1px 5px', cursor: 'pointer', marginRight: 4,
        }}>Open</button>
        <span style={{ ...mono, fontSize: 8, color: statusColor }}>
          {statusIcon} {timeAgo(task.completedAt)}
        </span>
      </div>
      {expanded && (
        <div style={{ paddingLeft: 10, marginTop: 4 }}>
          {summary && (
            <div style={{
              ...mono, fontSize: 8, color: 'rgba(255,255,255,0.45)',
              background: 'rgba(255,255,255,0.02)', borderRadius: 4,
              padding: '5px 7px', marginBottom: 4, whiteSpace: 'pre-wrap',
            }}>
              {summary}
            </div>
          )}
          {task.steps?.map((step, i) => (
            <div key={i} style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.25)', marginBottom: 1 }}>
              {step.toolEmoji || '⚡'} {step.toolLabel || step.tool} → {(step.result || step.input || '').slice(0, 60)}
            </div>
          ))}
          {task.result && (
            <div style={{
              ...mono, fontSize: 9, color: 'rgba(255,255,255,0.5)',
              background: 'rgba(255,255,255,0.02)', borderRadius: 4,
              padding: '6px 8px', marginTop: 4, maxHeight: 120, overflowY: 'auto',
              whiteSpace: 'pre-wrap',
            }}>
              {String(task.result).length > COMPLETED_TASK_RESULT_PREVIEW_LIMIT
                ? `${String(task.result).slice(0, COMPLETED_TASK_RESULT_PREVIEW_LIMIT)}…`
                : task.result}
            </div>
          )}
          <button onClick={() => navigator.clipboard?.writeText(task.result || '')}
            style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.3)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4, padding: 0 }}>
            📋 Copy
          </button>
        </div>
      )}
    </div>
  );
}

export default function TaskManager() {
  const open = useWorldStore((s) => s.taskManagerOpen);
  if (!open) return null;
  return <TaskManagerContent />;
}

function TaskManagerContent() {
  const agents = useWorldStore((s) => s.agents);
  const taskQueues = useWorldStore((s) => s.taskQueues);
  const activeTasks = useWorldStore((s) => s.activeTasks);
  const optimisticToolUse = useWorldStore((s) => s.optimisticToolUse);
  const optimisticTaskClosures = useWorldStore((s) => s.optimisticTaskClosures);
  const completedTasks = useWorldStore((s) => s.completedTasks);
  const enqueueTask = useWorldStore((s) => s.enqueueTask);
  const deleteQueuedTask = useWorldStore((s) => s.deleteQueuedTask);
  const editQueuedTask = useWorldStore((s) => s.editQueuedTask);
  const openResultModal = useWorldStore((s) => s.openResultModal);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addAgentId, setAddAgentId] = useState(agents[0]?.id || '');
  const [addInstruction, setAddInstruction] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [completedVisibleCount, setCompletedVisibleCount] = useState(COMPLETED_TASK_PAGE_SIZE);

  const hasOptimisticClosure = (agentId) =>
    Object.keys(optimisticTaskClosures).some((closureAgentId) =>
      areEquivalentAgentIds(closureAgentId, agentId)
    );
  const hasEquivalentActiveTask = (agentId) =>
    Object.entries(activeTasks).some(
      ([activeAgentId, task]) =>
        task?.status === 'running' && areEquivalentAgentIds(activeAgentId, agentId)
    );

  const allActive = Object.entries(activeTasks).reduce((entries, [agentId, task]) => {
    if (task?.status !== 'running' || hasOptimisticClosure(agentId)) {
      return entries;
    }

    const canonicalAgentId = resolveWorldAgentId(agentId, agents) || agentId;
    if (entries.some(([existingAgentId]) => existingAgentId === canonicalAgentId)) {
      return entries;
    }

    entries.push([canonicalAgentId, task]);
    return entries;
  }, []);
  const activeAgentIds = new Set(allActive.map(([agentId]) => agentId));
  const optimisticActive = Object.entries(optimisticToolUse).reduce((entries, [agentId, toolUse]) => {
    if (toolUse?.status !== 'running' || hasEquivalentActiveTask(agentId)) {
      return entries;
    }

    const canonicalAgentId = resolveWorldAgentId(agentId, agents) || agentId;
    if (activeAgentIds.has(canonicalAgentId) || entries.some(([existingAgentId]) => existingAgentId === canonicalAgentId)) {
      return entries;
    }

    entries.push([canonicalAgentId, toolUse]);
    return entries;
  }, []);
  const allQueued = useMemo(
    () => Object.entries(taskQueues).flatMap(([aid, q]) => q.map((t) => ({ ...t, agentId: aid }))),
    [taskQueues]
  );
  const visibleCompletedTasks = showCompleted
    ? completedTasks.slice(0, completedVisibleCount)
    : [];
  const hiddenCompletedCount = Math.max(0, completedTasks.length - visibleCompletedTasks.length);
  const selectedAgent = agents.find((entry) => entry.id === addAgentId) || null;

  const handleAdd = () => {
    const instruction = addInstruction.trim();
    if (!instruction) return;
    const agent = agents.find((entry) => entry.id === addAgentId);
    if (!agent) return;
    const label = instruction.slice(0, 40) || instruction;
    queueTaskAndKickoff(agent, instruction, label);
    setAddInstruction('');
    setShowAddForm(false);
  };

  const handleRunNow = (task) => {
    deleteQueuedTask(task.id);
    enqueueTask(task.agentId, task.instruction, task.label);
    useWorldStore.getState().setAgentIndicator(task.agentId, { type: 'task', pulsing: true });
    setTimeout(() => {
      void runAgentQueue(task.agentId).catch((error) => {
        console.warn('[task-manager] task failed', task.agentId, task.id, error);
      });
    }, 100);
  };

  return (
    <div style={{
      position: 'fixed', top: 40, right: 0,
      width: 360, height: 'calc(100vh - 40px)',
      background: 'var(--glass-bg)',
      backdropFilter: 'blur(var(--glass-blur))',
      borderLeft: '1px solid var(--color-border)',
      zIndex: 50, display: 'flex', flexDirection: 'column',
      animation: 'slideInRight 0.2s ease-out',
    }}>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>

      {/* Header */}
      <div style={{ padding: '42px 14px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ ...mono, fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Task Manager
          </span>
        </div>
        <div style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.25)', marginTop: 2 }}>
          {allActive.length + optimisticActive.length} active · {allQueued.length} queued · {completedTasks.length} completed
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>

        {/* IN PROGRESS */}
        {(allActive.length > 0 || optimisticActive.length > 0) && (
          <>
            <div style={{ ...mono, fontSize: 9, color: '#fbbf24', marginBottom: 6 }}>
              ⚡ IN PROGRESS ({allActive.length + optimisticActive.length})
            </div>
            {allActive.map(([aid, task]) => {
              const agent = findAgent(agents, aid);
              return <ActiveTaskCard key={aid} task={task} agent={agent} />;
            })}
            {optimisticActive.map(([aid, toolUse]) => {
              const agent = findAgent(agents, aid);
              return <OptimisticToolUseCard key={`optimistic-${aid}`} toolUse={toolUse} agent={agent} />;
            })}
          </>
        )}

        {/* QUEUE */}
        <div style={{ ...mono, fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 8, marginBottom: 6 }}>
          📋 QUEUE ({allQueued.length})
        </div>
        {allQueued.length === 0 ? (
          <div style={{ ...mono, fontSize: 9, color: 'rgba(255,255,255,0.15)', padding: '8px 0' }}>
            No tasks queued yet.
          </div>
        ) : (
          allQueued.map((task) => {
            const agent = findAgent(agents, task.agentId);
            return (
              <QueuedTaskCard key={getTaskIdentity(task)} task={task} agent={agent}
                onRunNow={handleRunNow}
                onEdit={editQueuedTask}
                onDelete={deleteQueuedTask}
              />
            );
          })
        )}

        {/* Add task button */}
        {!showAddForm ? (
          <button onClick={() => setShowAddForm(true)} style={{
            width: '100%', padding: '8px 0', marginTop: 4,
            ...mono, fontSize: 9, color: 'rgba(255,255,255,0.3)',
            background: 'none', border: '1px dashed rgba(255,255,255,0.08)',
            borderRadius: 6, cursor: 'pointer',
          }}>
            + Add Task
          </button>
        ) : (
          <TaskComposerCard
            agents={agents}
            selectedAgentId={addAgentId}
            onSelectAgentId={setAddAgentId}
            showAgentSelector
            instruction={addInstruction}
            onInstructionChange={setAddInstruction}
            placeholder={getTaskComposerPlaceholder(
              selectedAgent?.currentZoneId,
              selectedAgent?.currentZoneId
            )}
            onSubmit={handleAdd}
            onCancel={() => setShowAddForm(false)}
            submitLabel="Add Task"
          />
        )}

        {/* COMPLETED */}
        <div style={{ marginTop: 12 }}>
          <button onClick={() => {
            setShowCompleted(!showCompleted);
            if (showCompleted) setCompletedVisibleCount(COMPLETED_TASK_PAGE_SIZE);
          }} style={{
            ...mono, fontSize: 9, color: 'rgba(255,255,255,0.3)',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}>
            {showCompleted ? '▾' : '▸'} COMPLETED ({completedTasks.length})
          </button>
          {showCompleted && visibleCompletedTasks.map((task) => {
            const agent = findAgent(agents, task.agentId);
            return <CompletedTaskCard key={getTaskIdentity(task)} task={task} agent={agent} onView={() => openResultModal(task)} />;
          })}
          {showCompleted && hiddenCompletedCount > 0 && (
            <button
              onClick={() => setCompletedVisibleCount((count) => count + COMPLETED_TASK_PAGE_SIZE)}
              style={{
                width: '100%', padding: '6px 0', marginTop: 4,
                ...mono, fontSize: 8, color: 'rgba(255,255,255,0.32)',
                background: 'none', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 6, cursor: 'pointer',
              }}
            >
              Show {Math.min(COMPLETED_TASK_PAGE_SIZE, hiddenCompletedCount)} more ({hiddenCompletedCount} hidden)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function actionBtn(color) {
  return {
    ...{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" },
    fontSize: 8, padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
    background: 'none', border: `1px solid ${typeof color === 'string' && color.startsWith('#') ? `${color}44` : color}`,
    color: typeof color === 'string' && color.startsWith('#') ? color : color,
  };
}
